import { DEFAULT_LOCALE, LOCALES, type Locale } from "@/kernel/locale";

// DSH-016: English lives at the root, every other language under a prefix of
// its own (Ukrainian at /ua). The App Router tree is app/[locale]/…; its
// segment is the public prefix for prefixed languages, and the proxy rewrites
// a root address to the default language's internal segment, which is never
// served publicly (a request for it is redirected to the root address).
type LocaleRoute = {
  /** Internal [locale] route segment. */
  segment: string;
  /** Public address prefix; "" for the default language. */
  prefix: string;
  /** hreflang value of the language's addresses. */
  hreflang: string;
};

export const LOCALE_ROUTES: Readonly<Record<Locale, LocaleRoute>> = {
  "en-US": { segment: "en", prefix: "", hreflang: "en" },
  "uk-UA": { segment: "ua", prefix: "/ua", hreflang: "uk" },
};

/** Segments for generateStaticParams of app/[locale]. */
export function localeSegments(): { locale: string }[] {
  return LOCALES.map((locale) => ({ locale: LOCALE_ROUTES[locale].segment }));
}

export function localeFromSegment(segment: string): Locale | null {
  return LOCALES.find((locale) => LOCALE_ROUTES[locale].segment === segment) ?? null;
}

// Browsers read "//host", "/\host" and slashes split by a tab or newline as
// another origin, so a leading run of them collapses to one "/".
const LEADING_SEPARATORS = /^[/\\\t\n\r]+/;

function rootRelative(path: string): string {
  return `/${path.replace(LEADING_SEPARATORS, "")}`;
}

/** The locale of a public pathname and the pathname without its prefix. */
export function localeFromPath(pathname: string): { locale: Locale; pathname: string } {
  for (const locale of LOCALES) {
    const { prefix } = LOCALE_ROUTES[locale];
    if (!prefix) continue;
    if (pathname === prefix) return { locale, pathname: "/" };
    if (pathname.startsWith(`${prefix}/`)) return { locale, pathname: rootRelative(pathname.slice(prefix.length)) };
  }
  return { locale: DEFAULT_LOCALE, pathname: rootRelative(pathname) };
}

/** The public address of a page in a language; accepts a neutral or a localized pathname. */
export function pathForLocale(pathname: string, locale: Locale): string {
  const neutral = localeFromPath(rootRelative(pathname)).pathname;
  const { prefix } = LOCALE_ROUTES[locale];
  if (!prefix) return neutral;
  return neutral === "/" ? prefix : `${prefix}${neutral}`;
}

/**
 * The address the language switch opens: the chosen language's clean address
 * with the current query, less any ?lang=, and the current anchor
 * ("/ua", "?campaign=demo&lang=ua", "#faq" gives "/ua?campaign=demo#faq").
 */
export function switchAddress(href: string, search: string, hash: string): string {
  const query = new URLSearchParams(search);
  query.delete("lang");
  const rest = query.toString();
  return `${href}${rest ? `?${rest}` : ""}${hash}`;
}

/** hreflang alternates of a page: every language plus x-default, the default language's address. */
export function languageAlternates(pathname: string): Record<string, string> {
  const alternates: Record<string, string> = {};
  for (const locale of LOCALES) alternates[LOCALE_ROUTES[locale].hreflang] = pathForLocale(pathname, locale);
  alternates["x-default"] = pathForLocale(pathname, DEFAULT_LOCALE);
  return alternates;
}
